import { ProcessingOverlay } from '@components'

import { BorderRadius, Color, FormItemsGroup, Space, Spacing, Text } from '@billy/design-system'
import { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'

import { useInvoiceComplianceMode } from '@features/invoices/hooks/useInvoiceComplianceMode'

import { getIsSubmitActionTypeCreate } from '../../../../utils/getIsSubmitActionTypeCreate'
import { useCustomerForm } from '../../contexts/customerFormContext'
import { CustomerTypeToggle } from '../../elements/CustomerTypeToggle'
import { Address } from '../Address'
import { ContactPersons } from '../ContactPersons'
import { OptionalItems } from '../OptionalItems'
import { PhoneNumber } from '../PhoneNumber'
import { ShippingAddress } from '../ShippingAddress'
import { BusinessId } from './elements/BusinessId'
import { CompanyName } from './elements/CompanyName'
import { CustomerEInvoicingAddress } from './elements/CustomerEInvoicingAddress'
import { VatNumber } from './elements/VatNumber'

export const CompanyCustomerModalBody = (): ReactElement => {
  const { t } = useTranslation()
  const { isLoading, isSubmitting, submitActionType, customerForm } = useCustomerForm()
  const { isFrenchComplianceMode } = useInvoiceComplianceMode()

  const shouldShowCustomerTypeToggle = getIsSubmitActionTypeCreate(submitActionType)
  const shouldShowProcessingOverlay = isLoading || isSubmitting
  const { watch } = customerForm
  const customerCountry = watch('address.country')
  const shouldShowEInvoicingSection = !isFrenchComplianceMode || customerCountry === 'FR'

  return (
    <>
      {shouldShowProcessingOverlay && (
        <ProcessingOverlay borderRadius={BorderRadius.R12} color={Color.White} withAnimation />
      )}
      <FormItemsGroup rowSpacing={Spacing.S12}>
        {shouldShowCustomerTypeToggle && <CustomerTypeToggle />}
        <CompanyName />
        <FormItemsGroup itemsInRow={2}>
          <BusinessId />
          <VatNumber />
        </FormItemsGroup>
        <PhoneNumber />
        <ContactPersons />
        {shouldShowEInvoicingSection && (
          <>
            <Text variant="h3">{t('customer_modal.form.e_invoice_address.header')}</Text>
            <CustomerEInvoicingAddress />
            <Space size="s8" />
          </>
        )}
        <Text variant="h3">{t('customer_modal.form.company_address.header')}</Text>
        <Address />
        <ShippingAddress />
        <OptionalItems />
      </FormItemsGroup>
    </>
  )
}
